import { useCallback, useEffect, useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useMutation } from '@tanstack/react-query';
import { usePatientCard } from '../../../../hooks/usePatientCard';
import {
    checkDmPatientEmail,
    linkExistingDmPatient,
    type DmPatient,
    type LinkExistingResult
} from '../../../../services/dmPatient';
import { DmShellLayout } from './DmShellLayout';
import { SuccessBanner, ErrorBanner } from './DmBanners';
import {
    DmField,
    dmInputClasses,
    dmInputErrorClasses,
    DmBtnPrimary,
    DmBtnSecondary
} from './DmFormPrimitives';
import type { ActionRunnerProps } from '../types';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FoundPatient {
    id: number;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
}

function resolveLinkErrorMessage(result: LinkExistingResult & { success: false }): string {
    if (result.reason === 'not_found') {
        return `No DM patient found with this email. ${result.message}`;
    }
    if (result.reason === 'already_linked') {
        return `This patient is already linked to Dental Monitoring. ${result.message}`;
    }
    if (result.reason === 'already_linked_elsewhere') {
        const suffix = result.otherPatientId
            ? ` The DM patient is already linked to local patient ID ${result.otherPatientId}.`
            : '';
        return `${result.message}${suffix}`;
    }
    return result.message;
}

interface LinkExistingDmPatientLandingProps extends ActionRunnerProps {
    inWorkflow?: boolean;
    onCompleted?: () => void;
}

function LinkExistingDmPatientLanding({
    patientId,
    onBack,
    inWorkflow = false,
    onCompleted
}: LinkExistingDmPatientLandingProps) {
    const { data: card, refetch: refetchCard } = usePatientCard(patientId);

    const [email, setEmail] = useState('');
    const [emailTouched, setEmailTouched] = useState(false);
    const [found, setFound] = useState<FoundPatient | null>(null);
    const [notFound, setNotFound] = useState(false);
    const [linkedPatient, setLinkedPatient] = useState<DmPatient | null>(null);
    const prefilled = useRef(false);

    // Prefill the lookup email from the local patient card. The "Link to
    // existing" flow only mutates locally (no DM write until the user clicks
    // "Look up" then "Link patient"), so prefilling is safe and saves typing
    // for the common case where the same email is used in both systems.
    useEffect(() => {
        if (card && !prefilled.current && card.email) {
            prefilled.current = true;
            setEmail(card.email);
        }
    }, [card]);

    const emailError =
        emailTouched && email.trim() !== '' && !EMAIL_REGEX.test(email.trim())
            ? 'Enter a valid email address.'
            : undefined;

    const checkMutation = useMutation({
        mutationFn: (emailValue: string) => checkDmPatientEmail(patientId, emailValue),
        onSuccess: (result) => {
            if (result.exists) {
                setFound(result.dmPatient);
                setNotFound(false);
            } else {
                setFound(null);
                setNotFound(true);
            }
        }
    });

    const linkMutation = useMutation({
        mutationFn: (emailValue: string) => linkExistingDmPatient(patientId, emailValue),
        onSuccess: async (result) => {
            if (result.success) {
                await refetchCard();
                setLinkedPatient(result.dmPatient);
                onCompleted?.();
            }
        }
    });

    const isCheckLoading = checkMutation.isPending;
    const isLinkLoading = linkMutation.isPending;
    const isAnyLoading = isCheckLoading || isLinkLoading;

    const canLookUp = !!email.trim() && EMAIL_REGEX.test(email.trim()) && !isAnyLoading;

    const handleEmailChange = useCallback(
        (value: string) => {
            setEmail(value);
            setFound(null);
            setNotFound(false);
            checkMutation.reset();
            linkMutation.reset();
        },
        [checkMutation, linkMutation]
    );

    const handleLookUp = useCallback(() => {
        setEmailTouched(true);
        if (!EMAIL_REGEX.test(email.trim())) return;
        checkMutation.reset();
        linkMutation.reset();
        setNotFound(false);
        setFound(null);
        checkMutation.mutate(email.trim());
    }, [email, checkMutation, linkMutation]);

    const handleLink = useCallback(() => {
        if (!found) return;
        linkMutation.reset();
        linkMutation.mutate(email.trim());
    }, [email, found, linkMutation]);

    const handleRetry = useCallback(() => {
        checkMutation.reset();
        linkMutation.reset();
        setNotFound(false);
    }, [checkMutation, linkMutation]);

    const checkErrorMessage =
        checkMutation.error instanceof Error ? checkMutation.error.message : null;

    const linkResultError =
        linkMutation.data && !linkMutation.data.success
            ? resolveLinkErrorMessage(linkMutation.data)
            : null;

    const linkNetworkError =
        linkMutation.error instanceof Error ? linkMutation.error.message : null;

    const submitError = linkResultError ?? linkNetworkError ?? checkErrorMessage;

    const content = (
        <div className="w-full max-w-2xl mx-auto flex flex-col gap-4">
            {/*  Success state  */}
            {linkedPatient && (
                <>
                    <SuccessBanner
                        message={`Linked to existing DM patient (DM #${linkedPatient.id}).`}
                    />
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
                            Linked DM patient
                        </p>
                        <p className="text-sm font-semibold text-gray-900">
                            {[linkedPatient.firstName, linkedPatient.lastName]
                                .filter(Boolean)
                                .join(' ') || '—'}
                        </p>
                        <p className="text-sm text-gray-500">{linkedPatient.email ?? '—'}</p>
                        <div className="mt-3 grid grid-cols-[110px_1fr] gap-y-1 text-xs">
                            <span className="text-gray-400">DM ID</span>
                            <span className="font-mono text-gray-700">{linkedPatient.id}</span>
                            {linkedPatient.externalId && (
                                <>
                                    <span className="text-gray-400">External ID</span>
                                    <span className="font-mono text-gray-700">
                                        {linkedPatient.externalId}
                                    </span>
                                </>
                            )}
                            {linkedPatient.phone && (
                                <>
                                    <span className="text-gray-400">Phone</span>
                                    <span className="text-gray-700">{linkedPatient.phone}</span>
                                </>
                            )}
                            {linkedPatient.status && (
                                <>
                                    <span className="text-gray-400">Status</span>
                                    <span className="text-gray-700">{linkedPatient.status}</span>
                                </>
                            )}
                        </div>
                    </div>
                    <p className="text-sm text-gray-500 text-center">
                        Next step: send the patient an activation link so they can access the DM
                        app.
                    </p>
                    {!inWorkflow && (
                        <div className="flex justify-end pt-2">
                            <DmBtnSecondary onClick={onBack}>Back to actions</DmBtnSecondary>
                        </div>
                    )}
                </>
            )}

            {/*  Lookup + confirm flow  */}
            {!linkedPatient && (
                <>
                    {submitError && <ErrorBanner message={submitError} onRetry={handleRetry} />}

                    <div className="bg-white rounded-xl border border-gray-200 p-6 flex flex-col gap-4">
                        <DmField
                            label="DM patient email"
                            required
                            hint="Finds by patient in dental monitoring via email"
                            error={emailError}
                        >
                            <div className="flex gap-2">
                                <input
                                    type="email"
                                    className={`${emailError ? dmInputErrorClasses : dmInputClasses} flex-1`}
                                    placeholder="patient@example.com"
                                    value={email}
                                    onChange={(e) => handleEmailChange(e.target.value)}
                                    onBlur={() => setEmailTouched(true)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && canLookUp) {
                                            e.preventDefault();
                                            handleLookUp();
                                        }
                                    }}
                                    disabled={isAnyLoading}
                                />
                                <DmBtnPrimary onClick={handleLookUp} disabled={!canLookUp}>
                                    {isCheckLoading ? 'Looking up…' : 'Look up'}
                                </DmBtnPrimary>
                            </div>
                        </DmField>

                        {notFound && (
                            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                                <XMarkIcon className="w-4 h-4 flex-shrink-0" />
                                No DM patient found with that email.
                            </div>
                        )}

                        {found && (
                            <div className="p-4 border border-blue-200 bg-blue-50 rounded-xl">
                                <p className="text-[11px] font-semibold text-blue-500 uppercase tracking-wide mb-2">
                                    DM patient found
                                </p>
                                <p className="text-sm font-semibold text-gray-900">
                                    {[found.firstName, found.lastName].filter(Boolean).join(' ') ||
                                        '—'}
                                </p>
                                <p className="text-sm text-gray-500">{found.email ?? '—'}</p>
                                <p className="text-xs text-gray-400 mt-1">
                                    DM ID: <span className="font-mono">{found.id}</span>
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                        {!inWorkflow && (
                            <DmBtnSecondary onClick={onBack} disabled={isAnyLoading}>
                                Cancel
                            </DmBtnSecondary>
                        )}
                        {found && (
                            <DmBtnPrimary onClick={handleLink} disabled={isAnyLoading}>
                                {isLinkLoading ? 'Linking…' : 'Link patient'}
                            </DmBtnPrimary>
                        )}
                    </div>
                </>
            )}
        </div>
    );

    if (inWorkflow) return content;

    return (
        <DmShellLayout title="Link to existing DM patient" onBack={onBack}>
            {content}
        </DmShellLayout>
    );
}

export { LinkExistingDmPatientLanding };
